import { useEffect } from "react";

/**
 * DevToolsProtection Component
 *
 * Disables developer tools in production (non-local) environments.
 * Works across OS: Windows (7/8/10/11/Vista), macOS, Linux, Unix.
 * DevTools remain available only on local: localhost, 127.0.0.1, and private LAN ranges.
 *
 * Blocked shortcuts (Windows/Linux + Mac):
 * - F12, Ctrl+F12, Cmd+F12
 * - Ctrl+Shift+I / Cmd+Option+I (DevTools)
 * - Ctrl+Shift+J / Cmd+Option+J (Console)
 * - Ctrl+Shift+C / Cmd+Option+C (Inspect)
 * - Ctrl+U / Cmd+Option+U (View Source)
 * - Ctrl+Shift+K / Cmd+Option+K (Firefox Console)
 * - Ctrl+Shift+P / Cmd+Shift+P (Print)
 * - Right-click context menu (removes "Inspect")
 *
 * Detection: resize + console check; warns and redirects if DevTools are open.
 *
 * Secret unlock (production only): Ctrl+Alt+Shift+D (Windows/Linux) or
 * Cmd+Option+Shift+D (macOS) toggles protection off for this tab so you can use
 * normal DevTools shortcuts (e.g. Inspect) and the context menu. Press again to
 * re-lock. Ignored while focus is in an input, textarea, or contenteditable field.
 *
 * Cannot be blocked from JS (browser/OS UI): menu bar → Developer Tools,
 * address bar view-source:, or DevTools already open before page load.
 */
const DevToolsProtection = () => {
  useEffect(() => {
    const hostname = window.location.hostname;

    const isLocalhost = () => {
      if (
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname === "[::1]" ||
        hostname.startsWith("192.168.") ||
        hostname.startsWith("10.")
      ) {
        return true;
      }
      if (hostname.startsWith("172.")) {
        const second = hostname.slice(4).split(".")[0];
        const n = parseInt(second, 10);
        return n >= 16 && n <= 31;
      }
      return false;
    };

    if (isLocalhost()) {
      return;
    }

    let devToolsUnlocked = false;

    const isEditableTarget = (target) => {
      if (!target || !target.closest) return false;
      const el = target.closest("input, textarea, select, [contenteditable='true']");
      return Boolean(el);
    };

    const toggleSecretUnlock = (e) => {
      if (isEditableTarget(e.target)) return false;
      const k = e.keyCode;
      const isD = k === 68 || (e.key != null && e.key.toLowerCase() === "d");
      if (!isD) return false;
      const winChord = e.ctrlKey && e.altKey && e.shiftKey;
      const macChord = e.metaKey && e.altKey && e.shiftKey;
      if (!winChord && !macChord) return false;
      devToolsUnlocked = !devToolsUnlocked;
      e.preventDefault();
      e.stopPropagation();
      return true;
    };

    const disableRightClick = (e) => {
      if (devToolsUnlocked) return;
      e.preventDefault();
      return false;
    };

    const stop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const key = (e) => (e.key != null ? e.key.toLowerCase() : "");
    const code = (e) => e.keyCode;

    const disableKeyboardShortcuts = (e) => {
      if (toggleSecretUnlock(e)) return;
      if (devToolsUnlocked) return;

      const k = code(e);

      // F12 (and Ctrl+F12 / Cmd+F12 on some browsers)
      if (k === 123 || key(e) === "f12") {
        return stop(e);
      }

      // I - DevTools (Ctrl+Shift+I / Cmd+Option+I)
      if ((e.ctrlKey && e.shiftKey && k === 73) || (e.metaKey && e.altKey && k === 73)) {
        return stop(e);
      }
      if (key(e) === "i" && (e.ctrlKey && e.shiftKey || e.metaKey && e.altKey)) {
        return stop(e);
      }

      // J - Console (Ctrl+Shift+J / Cmd+Option+J)
      if ((e.ctrlKey && e.shiftKey && k === 74) || (e.metaKey && e.altKey && k === 74)) {
        return stop(e);
      }
      if (key(e) === "j" && (e.ctrlKey && e.shiftKey || e.metaKey && e.altKey)) {
        return stop(e);
      }

      // C - Inspect (Ctrl+Shift+C / Cmd+Option+C)
      if ((e.ctrlKey && e.shiftKey && k === 67) || (e.metaKey && e.altKey && k === 67)) {
        return stop(e);
      }
      if (key(e) === "c" && (e.ctrlKey && e.shiftKey || e.metaKey && e.altKey)) {
        return stop(e);
      }

      // U - View Source (Ctrl+U / Cmd+Option+U)
      if ((e.ctrlKey && k === 85) || (e.metaKey && e.altKey && k === 85)) {
        return stop(e);
      }
      if (key(e) === "u" && (e.ctrlKey || (e.metaKey && e.altKey))) {
        return stop(e);
      }

      // K - Firefox Console (Ctrl+Shift+K / Cmd+Option+K)
      if ((e.ctrlKey && e.shiftKey && k === 75) || (e.metaKey && e.altKey && k === 75)) {
        return stop(e);
      }
      if (key(e) === "k" && (e.ctrlKey && e.shiftKey || e.metaKey && e.altKey)) {
        return stop(e);
      }

      // P with Shift - Print/some DevTools (Ctrl+Shift+P / Cmd+Shift+P)
      if ((e.ctrlKey && e.shiftKey && k === 80) || (e.metaKey && e.shiftKey && k === 80)) {
        return stop(e);
      }
      if (key(e) === "p" && (e.ctrlKey && e.shiftKey || e.metaKey && e.shiftKey)) {
        return stop(e);
      }
    };

    // Detect if DevTools are open
    const detectDevTools = () => {
      let devtools = false;
      const threshold = 160; // Height difference threshold

      const checkDevTools = () => {
        if (devToolsUnlocked) return;
        if (
          window.outerHeight - window.innerHeight > threshold ||
          window.outerWidth - window.innerWidth > threshold
        ) {
          if (!devtools) {
            devtools = true;
            // Option 1: Close the page (uncomment if you want this behavior)
            // window.location.href = "about:blank";
            
            // Option 2: Show warning and redirect (recommended)
            alert("Developer tools are not allowed in production.");
            window.location.href = window.location.origin;
          }
        } else {
          devtools = false;
        }
      };

      // Check periodically
      const interval = setInterval(checkDevTools, 500);

      // Also check on resize
      window.addEventListener("resize", checkDevTools);

      return () => {
        clearInterval(interval);
        window.removeEventListener("resize", checkDevTools);
      };
    };

    // Console detection (another method)
    const detectConsole = () => {
      let devtools = false;
      const element = new Image();
      Object.defineProperty(element, "id", {
        get: function () {
          if (devToolsUnlocked) return "";
          devtools = true;
          if (devtools) {
            alert("Developer tools are not allowed in production.");
            window.location.href = window.location.origin;
          }
          return "";
        },
      });

      const checkConsole = () => {
        if (devToolsUnlocked) return;
        devtools = false;
        console.log(element);
        console.clear();
      };

      const interval = setInterval(checkConsole, 1000);
      return () => clearInterval(interval);
    };

    // Capture phase so we block before other handlers
    document.addEventListener("contextmenu", disableRightClick, true);
    document.addEventListener("keydown", disableKeyboardShortcuts, true);

    // Start DevTools detection
    const cleanupDevTools = detectDevTools();
    const cleanupConsole = detectConsole();

    // Cleanup on unmount (same capture flag as addEventListener)
    return () => {
      document.removeEventListener("contextmenu", disableRightClick, true);
      document.removeEventListener("keydown", disableKeyboardShortcuts, true);
      cleanupDevTools();
      cleanupConsole();
    };
  }, []);

  return null; // This component doesn't render anything
};

export default DevToolsProtection;
